import type { FormAnswers } from "@bjalon/form-runtime";
import type {
  SessionPublicDeck,
  SessionPublicExercise,
  SessionPublicProjection,
  SessionPublicQuestionnaire,
} from "../types/access";

type CachedProjection = Omit<
  SessionPublicProjection,
  "publicEndAt" | "publicStartAt"
> & {
  publicEndAtIso: string | null;
  publicStartAtIso: string | null;
};

type CachedQuestionnaire = Omit<
  SessionPublicQuestionnaire,
  "closedAt" | "closedToNewEntriesAt" | "correctionVisibleAt" | "openedAt"
> & {
  closedAtIso: string | null;
  closedToNewEntriesAtIso: string | null;
  correctionVisibleAtIso: string | null;
  openedAtIso: string | null;
};

type CachedExercise = Omit<
  SessionPublicExercise,
  "activatedAt" | "correctionVisibleAt" | "passedAt"
> & {
  activatedAtIso: string | null;
  correctionVisibleAtIso: string | null;
  passedAtIso: string | null;
};

type ParticipantSessionCacheDocument = {
  decks: SessionPublicDeck[];
  exercises: CachedExercise[];
  projection: CachedProjection | null;
  questionnaires: CachedQuestionnaire[];
  savedAtIso: string;
};

type ParticipantLocalQuestionnaireDraft = {
  answers: Record<string, unknown>;
  savedAtIso: string;
};

type ParticipantLocalExerciseDraft = {
  answerMarkdown: string;
  savedAtIso: string;
};

export type ParticipantSessionCache = {
  decks: SessionPublicDeck[];
  exercises: SessionPublicExercise[];
  projection: SessionPublicProjection | null;
  questionnaires: SessionPublicQuestionnaire[];
  savedAt: Date;
};

export type LocalQuestionnaireDraft = {
  answers: FormAnswers;
  savedAt: Date;
};

export type LocalExerciseDraft = {
  answerMarkdown: string;
  savedAt: Date;
};

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function dateToIso(value: Date | null): string | null {
  return value ? value.toISOString() : null;
}

function isoToDate(value: string | null): Date | null {
  return value ? new Date(value) : null;
}

function cacheKey(uid: string, sessionId: string): string {
  return `qorelia.participant.cache.${uid}.${sessionId}`;
}

function questionnaireDraftKey({
  questionnaireRuntimeId,
  sessionId,
  uid,
}: {
  questionnaireRuntimeId: string;
  sessionId: string;
  uid: string;
}): string {
  return `qorelia.participant.questionnaireDraft.${uid}.${sessionId}.${questionnaireRuntimeId}`;
}

function exerciseDraftKey({
  exerciseRuntimeId,
  sessionId,
  uid,
}: {
  exerciseRuntimeId: string;
  sessionId: string;
  uid: string;
}): string {
  return `qorelia.participant.exerciseDraft.${uid}.${sessionId}.${exerciseRuntimeId}`;
}

function projectionToCache(
  projection: SessionPublicProjection | null,
): CachedProjection | null {
  return projection
    ? {
        ...projection,
        publicEndAtIso: dateToIso(projection.publicEndAt),
        publicStartAtIso: dateToIso(projection.publicStartAt),
      }
    : null;
}

function projectionFromCache(
  projection: CachedProjection | null,
): SessionPublicProjection | null {
  return projection
    ? {
        ...projection,
        publicEndAt: isoToDate(projection.publicEndAtIso),
        publicStartAt: isoToDate(projection.publicStartAtIso),
      }
    : null;
}

function questionnaireToCache(
  questionnaire: SessionPublicQuestionnaire,
): CachedQuestionnaire {
  return {
    ...questionnaire,
    closedAtIso: dateToIso(questionnaire.closedAt),
    closedToNewEntriesAtIso: dateToIso(questionnaire.closedToNewEntriesAt),
    correctionVisibleAtIso: dateToIso(questionnaire.correctionVisibleAt),
    openedAtIso: dateToIso(questionnaire.openedAt),
  };
}

function questionnaireFromCache(
  questionnaire: CachedQuestionnaire,
): SessionPublicQuestionnaire {
  return {
    ...questionnaire,
    closedAt: isoToDate(questionnaire.closedAtIso),
    closedToNewEntriesAt: isoToDate(questionnaire.closedToNewEntriesAtIso),
    correctionVisibleAt: isoToDate(questionnaire.correctionVisibleAtIso),
    openedAt: isoToDate(questionnaire.openedAtIso),
  };
}

function exerciseToCache(exercise: SessionPublicExercise): CachedExercise {
  return {
    ...exercise,
    activatedAtIso: dateToIso(exercise.activatedAt),
    correctionVisibleAtIso: dateToIso(exercise.correctionVisibleAt),
    passedAtIso: dateToIso(exercise.passedAt),
  };
}

function exerciseFromCache(exercise: CachedExercise): SessionPublicExercise {
  return {
    ...exercise,
    activatedAt: isoToDate(exercise.activatedAtIso),
    correctionVisibleAt: isoToDate(exercise.correctionVisibleAtIso),
    passedAt: isoToDate(exercise.passedAtIso),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function saveParticipantSessionCache({
  decks,
  exercises,
  projection,
  questionnaires,
  sessionId,
  uid,
}: {
  decks: SessionPublicDeck[];
  exercises: SessionPublicExercise[];
  projection: SessionPublicProjection | null;
  questionnaires: SessionPublicQuestionnaire[];
  sessionId: string;
  uid: string;
}): void {
  const currentStorage = storage();

  if (!currentStorage) {
    return;
  }

  const document: ParticipantSessionCacheDocument = {
    decks,
    exercises: exercises.map(exerciseToCache),
    projection: projectionToCache(projection),
    questionnaires: questionnaires.map(questionnaireToCache),
    savedAtIso: new Date().toISOString(),
  };

  currentStorage.setItem(cacheKey(uid, sessionId), JSON.stringify(document));
}

export function readParticipantSessionCache({
  sessionId,
  uid,
}: {
  sessionId: string;
  uid: string;
}): ParticipantSessionCache | null {
  const rawValue = storage()?.getItem(cacheKey(uid, sessionId));

  if (!rawValue) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);

    if (!isRecord(parsed)) {
      return null;
    }

    return {
      decks: Array.isArray(parsed.decks)
        ? (parsed.decks as SessionPublicDeck[])
        : [],
      exercises: Array.isArray(parsed.exercises)
        ? (parsed.exercises as CachedExercise[]).map(exerciseFromCache)
        : [],
      projection: projectionFromCache(
        isRecord(parsed.projection)
          ? (parsed.projection as CachedProjection)
          : null,
      ),
      questionnaires: Array.isArray(parsed.questionnaires)
        ? (parsed.questionnaires as CachedQuestionnaire[]).map(
            questionnaireFromCache,
          )
        : [],
      savedAt:
        typeof parsed.savedAtIso === "string"
          ? new Date(parsed.savedAtIso)
          : new Date(0),
    };
  } catch {
    return null;
  }
}

export function saveLocalQuestionnaireDraft({
  answers,
  questionnaireRuntimeId,
  sessionId,
  uid,
}: {
  answers: FormAnswers;
  questionnaireRuntimeId: string;
  sessionId: string;
  uid: string;
}): LocalQuestionnaireDraft | null {
  const currentStorage = storage();

  if (!currentStorage) {
    return null;
  }

  const draft: ParticipantLocalQuestionnaireDraft = {
    answers: Object.fromEntries(Object.entries(answers)),
    savedAtIso: new Date().toISOString(),
  };

  currentStorage.setItem(
    questionnaireDraftKey({ questionnaireRuntimeId, sessionId, uid }),
    JSON.stringify(draft),
  );

  return {
    answers,
    savedAt: new Date(draft.savedAtIso),
  };
}

export function readLocalQuestionnaireDraft({
  questionnaireRuntimeId,
  sessionId,
  uid,
}: {
  questionnaireRuntimeId: string;
  sessionId: string;
  uid: string;
}): LocalQuestionnaireDraft | null {
  const rawValue = storage()?.getItem(
    questionnaireDraftKey({ questionnaireRuntimeId, sessionId, uid }),
  );

  if (!rawValue) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);

    if (!isRecord(parsed) || !isRecord(parsed.answers)) {
      return null;
    }

    return {
      answers: parsed.answers as FormAnswers,
      savedAt:
        typeof parsed.savedAtIso === "string"
          ? new Date(parsed.savedAtIso)
          : new Date(0),
    };
  } catch {
    return null;
  }
}

export function clearLocalQuestionnaireDraft({
  questionnaireRuntimeId,
  sessionId,
  uid,
}: {
  questionnaireRuntimeId: string;
  sessionId: string;
  uid: string;
}): void {
  storage()?.removeItem(
    questionnaireDraftKey({ questionnaireRuntimeId, sessionId, uid }),
  );
}

export function saveLocalExerciseDraft({
  answerMarkdown,
  exerciseRuntimeId,
  sessionId,
  uid,
}: {
  answerMarkdown: string;
  exerciseRuntimeId: string;
  sessionId: string;
  uid: string;
}): LocalExerciseDraft | null {
  const currentStorage = storage();

  if (!currentStorage) {
    return null;
  }

  const draft: ParticipantLocalExerciseDraft = {
    answerMarkdown,
    savedAtIso: new Date().toISOString(),
  };

  currentStorage.setItem(
    exerciseDraftKey({ exerciseRuntimeId, sessionId, uid }),
    JSON.stringify(draft),
  );

  return {
    answerMarkdown,
    savedAt: new Date(draft.savedAtIso),
  };
}

export function readLocalExerciseDraft({
  exerciseRuntimeId,
  sessionId,
  uid,
}: {
  exerciseRuntimeId: string;
  sessionId: string;
  uid: string;
}): LocalExerciseDraft | null {
  const rawValue = storage()?.getItem(
    exerciseDraftKey({ exerciseRuntimeId, sessionId, uid }),
  );

  if (!rawValue) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);

    if (!isRecord(parsed) || typeof parsed.answerMarkdown !== "string") {
      return null;
    }

    return {
      answerMarkdown: parsed.answerMarkdown,
      savedAt:
        typeof parsed.savedAtIso === "string"
          ? new Date(parsed.savedAtIso)
          : new Date(0),
    };
  } catch {
    return null;
  }
}

export function clearLocalExerciseDraft({
  exerciseRuntimeId,
  sessionId,
  uid,
}: {
  exerciseRuntimeId: string;
  sessionId: string;
  uid: string;
}): void {
  storage()?.removeItem(exerciseDraftKey({ exerciseRuntimeId, sessionId, uid }));
}
